/**
 * VistoeraTASK-00073 — "本地发布助手" (Local Publisher) tenant page.
 *
 * Product reframing of the former device-management table
 * (`LocalPublisherDevicesPage`, see git history) into a single-workspace
 * install/connect/publish state machine (task spec P2/P3). Reuses the
 * EXISTING Local Publisher backend contract unchanged
 * (`listLocalPublisherAgents` / `createLocalPublisherEnrollment` /
 * `revokeLocalPublisherAgent` — see `saas/api.ts` and
 * `backend/app/saas/services/local_publisher_agent_service.py`); no new
 * backend endpoints, DB tables, or credential formats were introduced.
 *
 * P0 (no third-party platform branding) / P3 (no Worker/capability/token
 * vocabulary) / P12 (lightweight — no polling while idle, no automation
 * runtime loaded here) are enforced directly in this component; see
 * `localPublisherAgentState.ts` for the pure state machine this renders.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AppleOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CopyOutlined,
  DesktopOutlined,
  FileImageOutlined,
  GlobalOutlined,
  LoadingOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  WarningFilled,
  WindowsOutlined,
} from '@ant-design/icons'
import { Alert, Button, Collapse, Popconfirm, Spin, Tooltip, Typography, message } from 'antd'
import {
  createLocalPublisherEnrollment,
  getLocalPublisherReleases,
  listLocalPublisherAgents,
  revokeLocalPublisherAgent,
  type LocalPublisherAgentOut,
  type LocalPublisherEnrollmentOut,
  type LocalPublisherReleaseOut,
  type LocalPublisherReleasesLatestOut,
} from '../api'
import { formatDateTime as formatDateTimeI18n } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import { formatBytes } from '../ui/bytesFormat'
import {
  deriveLocalPublisherDownloadUiState,
  deriveLocalPublisherPageState,
  detectLocalPublisherPlatform,
  formatReleaseDownloadMeta,
  isPairingExpired,
  localPublisherReleasePlatformKey,
  pairingSecondsRemaining,
  selectCanonicalInstallation,
  type LocalPublisherPlatform,
  type LocalPublisherReleasesFetchState,
} from './localPublisherAgentState'
import '../ui/tenant-items.css'
import './local-publisher-agent.css'

// VistoeraTASK-00082 P11 — one-click pairing deep link. Never a
// third-party platform's own custom scheme; this is the Local Publisher
// Agent's own installed-app protocol handler (registered by the real
// Windows installer's `HKCU\Software\Classes\vistoera-lpa` key — see
// `local_publisher_agent/installer`). Clicking it while the Agent is
// installed launches the Agent and redeems `pairing_code` locally,
// without the user ever copy/pasting a token by hand (P11/P12) — the
// visible pairing code below remains as the manual fallback for a first
// run where the OS hasn't associated the protocol yet.
function pairingDeepLink(pairingCode: string): string {
  return `vistoera-lpa://pair?code=${encodeURIComponent(pairingCode)}`
}

// P12 — lightweight polling policy: no polling at all while nothing is
// happening (STATE_A); a short interval only during an active pairing
// session (STATE_B); a slow background interval once an installation
// exists (STATE_C/D/E), paused whenever the tab is hidden.
const PAIRING_POLL_MS = 4000
const IDLE_STATUS_POLL_MS = 25000

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function LocalPublisherAgentPage() {
  const { t } = useTranslation(['localPublisher', 'actions'])
  const { locale } = useUiLocale()
  const { workspaceId = '' } = useParams()

  const [agents, setAgents] = useState<LocalPublisherAgentOut[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pairing, setPairing] = useState<LocalPublisherEnrollmentOut | null>(null)
  const [pairingCreating, setPairingCreating] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [releases, setReleases] = useState<LocalPublisherReleasesLatestOut | null>(null)
  const [releasesFetchState, setReleasesFetchState] = useState<LocalPublisherReleasesFetchState>('loading')

  // P3 — OS auto-detection, UX-only (never authorization/security; see
  // `detectLocalPublisherPlatform`'s own docstring). Computed once per
  // mount — the user's own OS doesn't change mid-session.
  const detectedPlatform = useMemo<LocalPublisherPlatform>(() => {
    if (typeof navigator === 'undefined') return 'unknown'
    const nav = navigator as Navigator & { userAgentData?: { platform?: string } }
    return detectLocalPublisherPlatform({
      uaDataPlatform: nav.userAgentData?.platform ?? null,
      navigatorPlatform: nav.platform ?? null,
      userAgent: nav.userAgent ?? null,
    })
  }, [])

  const loadReleases = useCallback(() => {
    setReleasesFetchState('loading')
    return getLocalPublisherReleases()
      .then(({ data }) => {
        setReleases(data)
        setReleasesFetchState('loaded')
      })
      .catch(() => {
        // VistoeraTASK-00105 — a failed fetch must never masquerade as
        // "版本准备中" (genuine unavailable). Distinct error state with
        // retry instead of silent null → preparing.
        setReleases(null)
        setReleasesFetchState('error')
      })
  }, [])

  // P26 — real release metadata (never a hardcoded env-var URL). Fetched
  // once; this rarely changes within a session and this page already
  // avoids polling while idle (P12).
  useEffect(() => {
    loadReleases()
  }, [loadReleases])

  const refresh = useCallback(async (): Promise<LocalPublisherAgentOut[] | null> => {
    if (!workspaceId) return null
    try {
      const { data } = await listLocalPublisherAgents(workspaceId)
      setAgents(data.items)
      setLoadError(null)
      return data.items
    } catch (err) {
      setLoadError((err as Error).message || t('localPublisher:status.loadFailed'))
      return null
    }
  }, [workspaceId, t])

  useEffect(() => {
    refresh()
  }, [refresh])

  const pairingActive = pairing !== null && !isPairingExpired(pairing.expires_at, nowMs)
  const pairingExpired = pairing !== null && isPairingExpired(pairing.expires_at, nowMs)

  // Once any (non-revoked) installation shows up for this workspace, the
  // pairing session's job is done — the agent's own status now drives the
  // page (see localPublisherAgentState.ts).
  useEffect(() => {
    if (pairing && agents && agents.length > 0) {
      setPairing(null)
    }
  }, [agents, pairing])

  // 1s ticker only while a pairing session exists (countdown display).
  useEffect(() => {
    if (!pairing) return
    const id = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [pairing])

  // Background status polling — see PAIRING_POLL_MS/IDLE_STATUS_POLL_MS
  // comment above. Explicitly does nothing while STATE_A is idle.
  useEffect(() => {
    const hasInstallation = agents !== null && agents.length > 0
    if (!pairingActive && !hasInstallation) return
    const intervalMs = pairingActive ? PAIRING_POLL_MS : IDLE_STATUS_POLL_MS
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      refresh()
    }, intervalMs)
    return () => clearInterval(id)
  }, [pairingActive, agents, refresh])

  const pageState = useMemo(
    () => deriveLocalPublisherPageState(agents, { pairingActive }),
    [agents, pairingActive],
  )

  const onConnect = async () => {
    setPairingCreating(true)
    try {
      const { data } = await createLocalPublisherEnrollment(workspaceId)
      setPairing(data)
      setNowMs(Date.now())
    } catch (err) {
      message.error((err as Error).message || t('localPublisher:connect.createFailed'))
    } finally {
      setPairingCreating(false)
    }
  }

  const onCancelPairing = () => setPairing(null)

  const onCopyPairingCode = () => {
    if (!pairing) return
    navigator.clipboard?.writeText(pairing.pairing_code).then(
      () => message.success(t('localPublisher:connect.copySuccess')),
      () => message.warning(t('localPublisher:connect.copyFailed')),
    )
  }

  const onDisconnect = async (agentId: string) => {
    setDisconnecting(true)
    try {
      await revokeLocalPublisherAgent(workspaceId, agentId)
      message.success(t('localPublisher:status.disconnectSuccess'))
      await refresh()
    } catch (err) {
      message.error((err as Error).message || t('localPublisher:status.disconnectFailed'))
    } finally {
      setDisconnecting(false)
    }
  }

  const onManualRetry = async () => {
    setRetrying(true)
    const items = await refresh()
    setRetrying(false)
    const canonical = selectCanonicalInstallation(items)
    if (canonical && canonical.status === 'offline') {
      message.info(t('localPublisher:status.stillOffline'))
    }
  }

  const badge = useMemo(() => {
    switch (pageState.kind) {
      case 'connected':
        return { dot: 'lpa-dot-online', label: t('localPublisher:status.connected') }
      case 'connecting':
        return { dot: 'lpa-dot-warning', label: t('localPublisher:connect.connectingBadge') }
      case 'update_required':
        return { dot: 'lpa-dot-warning', label: t('localPublisher:status.updateTitle') }
      default:
        return { dot: 'lpa-dot-offline', label: t('localPublisher:status.notConnectedBadge') }
    }
  }, [pageState.kind, t])

  const downloadButton = (kind: 'mac' | 'win') => {
    const platform = localPublisherReleasePlatformKey(kind)
    const release: LocalPublisherReleaseOut | undefined = releases?.platforms[platform]
    const uiState = deriveLocalPublisherDownloadUiState(releasesFetchState, release)
    const ready = uiState === 'ready'
    const icon = kind === 'mac' ? <AppleOutlined /> : <WindowsOutlined />
    const title = kind === 'mac' ? t('localPublisher:download.macTitle') : t('localPublisher:download.winTitle')
    const subtitle = kind === 'mac' ? t('localPublisher:download.macSubtitle') : t('localPublisher:download.winSubtitle')
    const notReady = kind === 'mac' ? t('localPublisher:download.macNotReady') : t('localPublisher:download.winNotReady')
    // P3/P5 — the auto-detected OS's button gets primary visual emphasis;
    // the other platform's download stays reachable either way (P4) — it
    // is never hidden, disabled, or removed just because it isn't the
    // detected OS. P20/P21 — agent connection state does not disable download.
    const recommended = detectedPlatform === platform

    let label = title
    if (uiState === 'loading') label = t('localPublisher:download.loading')
    else if (uiState === 'error') label = t('localPublisher:download.loadFailed')
    else if (!ready) label = notReady

    const btn = (
      <Button
        className={`lpa-download-btn${recommended ? ' lpa-download-btn-recommended' : ''}`}
        type={recommended && ready ? 'primary' : 'default'}
        icon={icon}
        disabled={uiState === 'loading' || uiState === 'unavailable'}
        href={ready ? release?.download_url : undefined}
        loading={uiState === 'loading'}
        onClick={uiState === 'error' ? () => loadReleases() : undefined}
      >
        <span className="lpa-download-btn-text">
          <span>
            {label}
            {ready && recommended && (
              <span className="lpa-download-btn-badge">{t('localPublisher:download.recommendedBadge')}</span>
            )}
            {uiState === 'error' && (
              <span className="lpa-download-btn-badge">{t('localPublisher:download.retry')}</span>
            )}
          </span>
          {ready && (
            <span className="lpa-download-btn-sub">
              {formatReleaseDownloadMeta(subtitle, release?.version, release?.size_bytes, formatBytes)}
            </span>
          )}
        </span>
      </Button>
    )

    if (ready) return btn
    const hint =
      uiState === 'error'
        ? t('localPublisher:download.loadFailedHint')
        : t('localPublisher:download.notReadyHint')
    return <Tooltip title={hint}>{btn}</Tooltip>
  }

  const renderConnectPanel = () => {
    if (pageState.kind === 'loading') {
      return (
        <div className="lpa-status-card">
          <Spin size="small" />
        </div>
      )
    }

    if (pageState.kind === 'connecting') {
      const remaining = pairing ? pairingSecondsRemaining(pairing.expires_at, nowMs) : 0
      return (
        <div className="lpa-status-card">
          <div className="lpa-status-title" style={{ marginBottom: 10 }}>
            <LoadingOutlined spin />
            {t('localPublisher:connect.connecting')}
          </div>
          {pairing && !pairingExpired && (
            <>
              <div style={{ marginBottom: 12 }}>
                <Button
                  className="lpa-deep-link-btn"
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  href={pairingDeepLink(pairing.pairing_code)}
                >
                  {t('localPublisher:connect.openAgentCta')}
                </Button>
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('localPublisher:connect.pairingCodeLabel')}
              </Typography.Text>
              <div className="lpa-pairing-code">
                <div className="lpa-pairing-code-value">{pairing.pairing_code}</div>
                <Button icon={<CopyOutlined />} onClick={onCopyPairingCode} />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('localPublisher:connect.expiresIn', { time: formatMmSs(remaining) })}
              </Typography.Text>
              <div style={{ marginTop: 12 }}>
                <Button onClick={onCancelPairing}>{t('localPublisher:connect.cancel')}</Button>
              </div>
            </>
          )}
          {pairingExpired && (
            <>
              <Alert type="warning" showIcon message={t('localPublisher:connect.expired')} description={t('localPublisher:connect.expiredHint')} style={{ marginBottom: 12 }} />
              <Button type="primary" loading={pairingCreating} onClick={onConnect}>
                {t('localPublisher:connect.regenerate')}
              </Button>
            </>
          )}
        </div>
      )
    }

    if (pageState.kind === 'connected') {
      const { installation, needsAttention, busy } = pageState
      return (
        <div className="lpa-status-card">
          <div className="lpa-status-head">
            <div className="lpa-status-title">
              <CheckCircleFilled style={{ color: 'var(--success-text)' }} />
              {busy ? t('localPublisher:status.connectedBusy') : t('localPublisher:status.connected')}
            </div>
            <Popconfirm
              title={t('localPublisher:status.disconnectConfirm')}
              onConfirm={() => onDisconnect(installation.id)}
            >
              <Button size="small" danger loading={disconnecting}>
                {t('localPublisher:status.disconnect')}
              </Button>
            </Popconfirm>
          </div>
          {needsAttention && (
            <Alert type="warning" showIcon icon={<WarningFilled />} message={t('localPublisher:status.attentionNeeded')} style={{ marginBottom: 10 }} />
          )}
          <div className="lpa-status-rows">
            {installation.os_name && (
              <div className="lpa-status-row">
                <span>{t('localPublisher:status.currentSystem')}</span>
                <span className="lpa-status-row-value">{installation.os_name}</span>
              </div>
            )}
            {installation.agent_version && (
              <div className="lpa-status-row">
                <span>{t('localPublisher:status.agentVersion')}</span>
                <span className="lpa-status-row-value">{installation.agent_version}</span>
              </div>
            )}
            <div className="lpa-status-row">
              <span>{t('localPublisher:status.lastConnected')}</span>
              <span className="lpa-status-row-value">
                {installation.last_seen_at ? formatDateTimeI18n(installation.last_seen_at, locale) : t('localPublisher:status.never')}
              </span>
            </div>
          </div>
        </div>
      )
    }

    if (pageState.kind === 'offline') {
      const { installation } = pageState
      return (
        <div className="lpa-status-card">
          <div className="lpa-status-title" style={{ marginBottom: 6 }}>
            <ClockCircleOutlined style={{ color: 'var(--text-muted)' }} />
            {t('localPublisher:status.offlineTitle')}
          </div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
            {t('localPublisher:status.offlineHint')}
          </Typography.Paragraph>
          <div className="lpa-status-rows" style={{ marginBottom: 12 }}>
            <div className="lpa-status-row">
              <span>{t('localPublisher:status.lastConnected')}</span>
              <span className="lpa-status-row-value">
                {installation.last_seen_at ? formatDateTimeI18n(installation.last_seen_at, locale) : t('localPublisher:status.never')}
              </span>
            </div>
          </div>
          <Button icon={<ReloadOutlined />} loading={retrying} onClick={onManualRetry}>
            {t('localPublisher:status.reconnect')}
          </Button>
        </div>
      )
    }

    if (pageState.kind === 'update_required') {
      return (
        <div className="lpa-status-card">
          <div className="lpa-status-title" style={{ marginBottom: 6 }}>
            <WarningFilled style={{ color: 'var(--warning-text)' }} />
            {t('localPublisher:status.updateTitle')}
          </div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
            {t('localPublisher:status.updateHint')}
          </Typography.Paragraph>
          <div className="lpa-step-actions">
            {downloadButton('mac')}
            {downloadButton('win')}
          </div>
        </div>
      )
    }

    // not_connected — handled inline as Step 2's own CTA, no separate card.
    return null
  }

  const faqItems = [1, 2, 3, 4, 5].map((n) => ({
    key: String(n),
    label: t(`localPublisher:faq.q${n}`),
    children: <Typography.Paragraph style={{ marginBottom: 0 }}>{t(`localPublisher:faq.a${n}`)}</Typography.Paragraph>,
  }))

  return (
    <div className="lpa-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">
            {t('localPublisher:title')} <span className="tc-badge tc-badge-info">{t('localPublisher:badge')}</span>
          </h2>
          <p className="tc-page-desc" style={{ maxWidth: 640 }}>
            {t('localPublisher:subtitle')}
          </p>
        </div>
      </div>

      {loadError && <Alert type="error" message={loadError} showIcon closable onClose={() => setLoadError(null)} />}

      <div className="lpa-main">
        <div className="lpa-left">
          <div className="lpa-step">
            <div className="lpa-step-index">1</div>
            <div className="lpa-step-body">
              <p className="lpa-step-title">{t('localPublisher:steps.step1Title')}</p>
              <p className="lpa-step-desc">{t('localPublisher:steps.step1Desc')}</p>
              <div className="lpa-step-actions">
                {downloadButton('mac')}
                {downloadButton('win')}
              </div>
            </div>
          </div>

          <div className="lpa-step">
            <div className="lpa-step-index">2</div>
            <div className="lpa-step-body">
              <p className="lpa-step-title">{t('localPublisher:steps.step2Title')}</p>
              <p className="lpa-step-desc">{t('localPublisher:steps.step2Desc')}</p>
              {pageState.kind === 'not_connected' ? (
                <div className="lpa-step-actions">
                  <Button type="primary" icon={<DesktopOutlined />} loading={pairingCreating} onClick={onConnect}>
                    {t('localPublisher:connect.cta')}
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 10 }}>{renderConnectPanel()}</div>
              )}
            </div>
          </div>

          <div className="lpa-step">
            <div className="lpa-step-index">3</div>
            <div className="lpa-step-body">
              <p className="lpa-step-title">{t('localPublisher:steps.step3Title')}</p>
              <p className="lpa-step-desc">{t('localPublisher:steps.step3Desc')}</p>
            </div>
          </div>

          <div className="lpa-feature-strip">
            <div className="lpa-feature">
              <div className="lpa-feature-icon">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <p className="lpa-feature-title">{t('localPublisher:features.secureTitle')}</p>
                <p className="lpa-feature-desc">{t('localPublisher:features.secureDesc')}</p>
              </div>
            </div>
            <div className="lpa-feature">
              <div className="lpa-feature-icon">
                <ThunderboltOutlined />
              </div>
              <div>
                <p className="lpa-feature-title">{t('localPublisher:features.lightTitle')}</p>
                <p className="lpa-feature-desc">{t('localPublisher:features.lightDesc')}</p>
              </div>
            </div>
            <div className="lpa-feature">
              <div className="lpa-feature-icon">
                <GlobalOutlined />
              </div>
              <div>
                <p className="lpa-feature-title">{t('localPublisher:features.browserTitle')}</p>
                <p className="lpa-feature-desc">{t('localPublisher:features.browserDesc')}</p>
              </div>
            </div>
            <div className="lpa-feature">
              <div className="lpa-feature-icon">
                <LockOutlined />
              </div>
              <div>
                <p className="lpa-feature-title">{t('localPublisher:features.controlTitle')}</p>
                <p className="lpa-feature-desc">{t('localPublisher:features.controlDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lpa-right">
          <div className="lpa-illustration-card">
            <div className="lpa-laptop">
              <div className="lpa-laptop-screen">
                <div className="lpa-laptop-dots">
                  <div className="lpa-laptop-dot" />
                  <div className="lpa-laptop-dot" />
                  <div className="lpa-laptop-dot" />
                </div>
                <div className="lpa-laptop-body">
                  <DesktopOutlined />
                </div>
              </div>
              <div className="lpa-laptop-base" />
              <div className="lpa-connection-badge">
                <span className={`lpa-dot ${badge.dot}`} />
                {badge.label}
              </div>
            </div>
            <div className="lpa-content-chips">
              <span className="lpa-content-chip">
                <VideoCameraOutlined /> {t('localPublisher:illustration.videoPublish')}
              </span>
              <span className="lpa-content-chip">
                <FileImageOutlined /> {t('localPublisher:illustration.imagePublish')}
              </span>
              <span className="lpa-content-chip">{t('localPublisher:illustration.morePlatforms')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="tc-data-card lpa-faq">
        <div style={{ padding: '14px 16px 0' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('localPublisher:faq.title')}
          </Typography.Title>
        </div>
        <div style={{ padding: 16 }}>
          <Collapse ghost items={faqItems} />
        </div>
      </div>
    </div>
  )
}
